<template>
	<v-container fluid class="mt-2">
		<v-row justify="center">
			<v-col cols="12" sm="8" md="5">
				<div v-if="pageState === 'loading'" class="loading-wrap">
					<v-progress-circular indeterminate color="#15c39a"></v-progress-circular>
				</div>
				<SelfSignupExpired v-else-if="pageState === 'expired'" />
				<SelfSignupError v-else-if="pageState === 'error'" />
				<SelfSignupSuccess v-else-if="pageState === 'success'" :email="submittedEmail" />
				<div v-else-if="pageState === 'form'">
					<div class="title-container mb-6">
						<h1>Auto-inscription</h1>
						<p v-if="groupName" class="group-info">
							<template v-if="allowSelectGroup">
								Tu t'inscris auprès de
								<span class="group-name-highlight">{{ groupName }}</span>
								— choisis ton groupe ci-dessous.
							</template>
							<template v-else>
								Tu t'inscris au groupe
								<span class="group-name-highlight">{{ groupName }}</span>
							</template>
						</p>
					</div>
					<SelfSignupForm
						:allow-select-group="allowSelectGroup"
						:hierarchy="hierarchyTree"
						:loading="isSubmitting"
						:error-message="submitError"
						@clear-error="submitError = ''"
						@submit="handleSubmit"
					/>
				</div>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import SelfSignupExpired from './SelfSignupExpired.vue';
import SelfSignupError from './SelfSignupError.vue';
import SelfSignupSuccess from './SelfSignupSuccess.vue';
import SelfSignupForm from './SelfSignupForm.vue';
import { parseQueryParams } from '@/utils/selfSignup/parseQueryParams';
import { isLinkExpired } from '@/utils/selfSignup/isLinkExpired';
import { buildHierarchyTree } from '@/utils/selfSignup/buildHierarchyTree';

export default {
	name: 'SelfSignupPage',
	components: {
		SelfSignupExpired,
		SelfSignupError,
		SelfSignupSuccess,
		SelfSignupForm,
	},
	data: function () {
		return {
			pageState: 'loading',
			groupId: null,
			groupType: null,
			groupName: '',
			allowSelectGroup: false,
			expireUntil: null,
			hierarchyTree: [],
			isSubmitting: false,
			submitError: '',
			submittedEmail: '',
		};
	},
	created: function () {
		this.bootstrapPage();
	},
	methods: {
		/**
		 * Loads query params, checks expiry, fetches group (and hierarchy if needed).
		 * @created_at 29/09/2026 by AI (Composer)
		 */
		bootstrapPage: async function () {
			const params = parseQueryParams(this.$route.query);
			this.groupId = params.groupId;
			this.groupType = params.groupType;
			this.allowSelectGroup = params.allowSelectGroup;
			this.expireUntil = params.expireUntil;

			if (!this.groupId || !this.groupType) {
				this.pageState = 'error';
				return;
			}

			if (isLinkExpired(this.expireUntil)) {
				this.pageState = 'expired';
				return;
			}

			try {
				const groupResponse = await this.$http.get('/api/self-signup/group', {
					params: {
						group_id: this.groupId,
						group_type: this.groupType,
					},
				});
				const groupData = groupResponse.data && groupResponse.data.data;
				if (groupData == null) {
					this.pageState = 'error';
					return;
				}
				this.groupName = groupData.group_name;

				if (this.allowSelectGroup) {
					const loaded = await this.loadHierarchy(this.groupId);
					if (!loaded) {
						this.pageState = 'error';
						return;
					}
				}

				this.pageState = 'form';
			} catch (error) {
				console.error(error);
				this.pageState = 'error';
			}
		},

		/**
		 * Fetches hierarchy for enterprise and builds the tree.
		 * @created_at 29/09/2026 by AI (Composer)
		 */
		loadHierarchy: async function (enterpriseId) {
			const response = await this.$http.get('/api/self-signup/hierarchy', {
				params: { enterprise_id: enterpriseId },
			});
			const data = response.data && response.data.data;
			if (data == null) {
				return false;
			}
			this.hierarchyTree = buildHierarchyTree(data.enterprise, data.subsidiaries, data.departments, data.teams);
			return true;
		},

		/**
		 * Submits the self-signup form to the API.
		 * @created_at 29/09/2026 by AI (Composer)
		 */
		handleSubmit: async function (formData) {
			this.submitError = '';
			this.isSubmitting = true;

			const selectedGroup = formData.selectedGroup;
			const submitGroupId = this.allowSelectGroup && selectedGroup ? selectedGroup.id : this.groupId;
			const submitGroupType = this.allowSelectGroup && selectedGroup ? selectedGroup.type : this.groupType;

			try {
				await this.$http.post('/api/self-signup/student-submit', {
					firstName: formData.firstName,
					lastName: formData.lastName,
					email: formData.email,
					group_id: submitGroupId,
					group_type: submitGroupType,
					expire_until: this.expireUntil,
					signup_link: window.location.href,
				});
				this.submittedEmail = formData.email;
				this.pageState = 'success';
			} catch (error) {
				const response = error.response;
				const data = response && response.data;
				const frMessage = data && data.fr_message;
				const message = data && data.message;
				this.submitError = frMessage || message || 'Une erreur est survenue, veuillez réessayer.';
			} finally {
				this.isSubmitting = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.title-container {
	text-align: center;
}

.group-info {
	margin-top: 12px;
	color: #555;
	font-size: 16px;
	line-height: 1.45;
}

.group-name-highlight {
	font-weight: 700;
	color: var(--tag-violet-primary);
}

.loading-wrap {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 40vh;
}
</style>
